import { NavLink } from "react-router-dom";

import logo from "../../assets/img/logo/logo-white.svg";
import { ACTIVITY, APP } from "../../i18n/el";

// Πλαϊνό μενού πλοήγησης. Χτίζεται από utilities και όχι από συστατικό της
// βιβλιοθήκης, γιατί το HeroUI 3 δεν έχει Navbar ή Sidebar.
//
// Το PerfectScrollbar έφυγε, καθώς ενεργοποιούνταν μόνο σε Windows και η φυσική
// κύλιση αρκεί.

/** Ένας σύνδεσμος του μενού. Το `end` χρειάζεται στη ρίζα, αλλιώς είναι ενεργή παντού. */
function MenuLink({ route, onNavigate, badgeCount = 0 }) {
    return (
        <NavLink
            to={route.path}
            end={route.path === "/"}
            onClick={onNavigate}
            className={({ isActive }) =>
                [
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--menu-bg)]",
                    isActive
                        ? "bg-white/15 font-medium text-white"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                ].join(" ")
            }
        >
            <i className={`${route.icon} w-4 text-center`} aria-hidden="true" />
            <span>{route.name}</span>
            {/* Η σήμανση αδιάβαστων (ΛΑ-22, ΜΛΑ-04).
                Το πλήθος διαβάζεται **και από βοηθητική τεχνολογία**: μόνο του, ένα
                νούμερο δίπλα σε σύνδεσμο δεν λέει τι μετρά. Στο μηδέν δεν
                αποδίδεται τίποτα — σήμανση που είναι πάντοτε εκεί δεν μεταφέρει
                πληροφορία. */}
            {badgeCount > 0 && (
                <span className="ms-auto inline-flex min-w-5 items-center justify-center rounded-full bg-white px-1.5 py-0.5 text-xs font-semibold text-[var(--menu-bg)]">
                    <span aria-hidden="true">{badgeCount > 99 ? "99+" : badgeCount}</span>
                    {/* ⚠ **Η εκφώνηση έρχεται από τη ΔΙΑΔΡΟΜΗ**, όχι από σταθερά.
                        Ήταν καρφωμένη στο «αδιάβαστες ειδοποιήσεις», που ήταν
                        σωστό όσο υπήρχε **μία** σήμανση· με δεύτερη, ο
                        αναγνώστης οθόνης θα άκουγε «3 αδιάβαστες ειδοποιήσεις»
                        δίπλα στα Καθήκοντα. Ένα νούμερο χωρίς σωστό όνομα δεν
                        είναι απλώς φτωχό — είναι **ψευδές**. */}
                    <span className="sr-only">
                        {`${badgeCount} ${route.badgeLabel ?? ACTIVITY.unreadBadgeLabel}`}
                    </span>
                </span>
            )}
        </NavLink>
    );
}

/**
 * @param badges Πλήθη ανά κλειδί `route.badge`. Προεπιλογή `{}`, ώστε ο έλεγχος
 *   καλωδίωσης να μην το ζητά από κάθε καλούντα — και ώστε το μενού να αποδίδεται
 *   κανονικά όσο το πλήθος δεν έχει φορτώσει ακόμη.
 */
export default function MainMenu({ routes, onNavigate, badges = {} }) {
    // Το προφίλ και η αποσύνδεση ζουν στο μενού λογαριασμού της κεφαλίδας, οπότε
    // εδώ μένει μόνο η πλοήγηση περιεχομένου. Η εγγραφή «Logout» δεν καρφώνεται πλέον
    // με `position: absolute` σε σταθερή απόσταση από το κάτω μέρος — δεν υπάρχει.
    const main = routes.filter((route) => !route.hidden);

    return (
        <nav
            aria-label="Κύρια πλοήγηση"
            className="flex h-full flex-col bg-[var(--menu-bg)] px-3 py-5 [--menu-bg:var(--color-purple-950)]"
        >
            <NavLink
                to="/"
                onClick={onNavigate}
                className="mb-6 flex flex-col items-center gap-2 rounded-lg px-2 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--menu-bg)]"
            >
                <img src={logo} alt="" className="h-17 w-auto" />
                {/* Ο πλήρης τίτλος της εργασίας και όχι σύντμηση, σε 1.25rem. Σε πλάτος
                    288 πίξελ αναδιπλώνεται σε έξι γραμμές και το τμήμα ταυτότητας
                    καταλαμβάνει περίπου 210 πίξελ· γι' αυτό ο περιέκτης του μενού έχει
                    `overflow-y-auto`, ώστε σε κοντή οθόνη να κυλά αντί να κόβει τις
                    εγγραφές πλοήγησης. */}
                <span className="text-center text-xl leading-snug font-medium text-white/90">
                    {APP.title}
                </span>
            </NavLink>

            <ul className="flex flex-1 flex-col gap-1">
                {main.map((route) => (
                    <li key={route.path}>
                        <MenuLink
                            route={route}
                            onNavigate={onNavigate}
                            badgeCount={route.badge ? (badges[route.badge] ?? 0) : 0}
                        />
                    </li>
                ))}
            </ul>

        </nav>
    );
}
